import React from 'react';
import { ArrowDown } from '@fabric-ui/icons';
import { Avatar, Box, BoxProps, Skeleton, SxProps, Theme, useTheme } from '@mui/material';
import { sanitize, stringToColor } from '@utils/utils';

interface FoundCountResultsProps {
  label: string;
  count?: number;
  height?: number;
  loading?: boolean;
  style?: SxProps<Theme>;
}

export function FoundCountResults({ label, count = 0, height = 21, loading, style }: FoundCountResultsProps): React.JSX.Element {
  return loading ? (
    <Skeleton height={height} width={160} />
  ) : (
    <Box sx={{ color: theme => theme.palette.colors.neutrals[800], ...style }}>
      <strong>{count.toLocaleString()}</strong> {label} found
    </Box>
  );
}

interface CollapseArrowProps {
  isExpanded?: boolean;
}

export function CollapseArrow({ isExpanded = false }: CollapseArrowProps): React.JSX.Element {
  const theme = useTheme();
  return (
    <ArrowDown
      style={{
        transform: `rotate(${isExpanded ? 0 : -90}deg)`,
        transition: theme.transitions.create(['transform'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    />
  );
}

interface InitialsProps {
  name: string;
  size?: 'medium' | 'small' | 'xSmall';
}

export function Initials({ name, size = 'medium' }: InitialsProps): React.JSX.Element {
  const theme = useTheme();

  return (
    <Avatar
      sx={{
        backgroundColor: stringToColor(name, theme.palette.avatarColors),
        textTransform: 'uppercase',
        fontWeight: 600,
        ...(size === 'small' ? { width: 30, height: 30, fontSize: 13 } : size === 'xSmall' ? { width: 24, height: 24, fontSize: 12 } : {}),
      }}>
      {name.split(' ')[0]?.[0]}
      {name.split(' ')[1]?.[0]}
    </Avatar>
  );
}

interface SanitizeHTMLProps extends BoxProps {
  html: string;
}

const styleHTML = {
  'pre, code': {
    backgroundColor: ({ palette }) => palette.colors.neutrals[200],
    color: 'inherit',
  },
  pre: {
    whiteSpace: 'pre-wrap',
    my: '5px',
    padding: '5px 10px',
  },
  code: {
    fontSize: 13,
    padding: '2px 4px',
  },
  a: {
    color: ({ palette }) => palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
};

export function SanitizeHTML({ html, ...props }: SanitizeHTMLProps): React.JSX.Element {
  return <Box sx={styleHTML} dangerouslySetInnerHTML={{ __html: sanitize(html) }} {...props} />;
}
